<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'contractEvents',
    path: '/contract-events',
    component: 'ContractEvents',
    title: () => pgettext('ContractEvents|Title', 'Contract Events'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'ContractEvents',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const gridName = ref('contractEvents');
  const dsParams = ref({
    date_from: $fn.value.today(),
    date_to: null,
  });
  const selectedItems = ref([]);
  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'event_name',
      type: 'command',
      name: $fn.value.pgettext('ContractEvents', 'Name'),
    },
    {
      field: 'description',
      name: $fn.value.pgettext('ContractEvents', 'Description'),
    },
    {
      field: 'contract',
      name: $fn.value.pgettext('ContractEvents', 'Contract'),
      type: 'command',
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    {{ $value.contract_number }}\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'contract',
            navigateOnIconClick: true,
            params: {
              id: 'contract.id',
            },
          },
        ],
      },
    },
    {
      field: 'timestamp',
      name: $fn.value.pgettext('ContractEvents', 'Timestamp'),
      formatter: {
        name: 'datetime',
      },
    },
    {
      field: 'identity',
      name: $fn.value.pgettext('ContractEvents', 'User'),
      formatter: {
        name: 'user',
      },
    },
  ]);

  const $data = computed(() => ({
    gridName: gridName.value,
    dsParams: dsParams.value,
    selectedItems: selectedItems.value,
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_ds = () => {
    return $fn.value.gridDataSource('browse_contract_events', {
      parameters: () => dsParams.value,
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'gridName' },
      {
        name: 'dsParams',
        cache: { type: 'local', key: 'ContractEvents.dsParams' },
      },
      { name: 'selectedItems' },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      gridName,
      dsParams,
      selectedItems,
      ds,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('ContractEvents|Title', 'Contract Events'),
      icon: $config.value.contract.views.contractEvents.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_qqqkrool9_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2248226175642056_setData_3u4uf84el_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2248226175642056_showModal_ucffipva1_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'DataViewerModal',
      props: {
        content: $event.data.data.event_data,
        mode: 'json',
      },
    };
  };
  const inst_evh_2248226175642056_command_mv0rzcy96_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'event_name';
  };
  const act_2248226175642056_showModal_ztzuz1eqz_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Job',
      props: {
        oid: $event.data.data.id,
      },
    };
  };
  const inst_evh_2248226175642056_command_l4e39hc0q_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'number';
  };
  const act_2248226175642056_showModal_c9khoa9fq_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Job',
      props: {
        oid: $event.data.data.parent_id,
      },
    };
  };
  const inst_evh_2248226175642056_command_par7ncxnf_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'parent_number';
  };
  const act_4615685607825208_setData_5r1ewplww_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'dsParams.date_from',
      value: $event.data[0],
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_4615685607825208_setData_h3swu0vmm_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'dsParams.date_to',
      value: $event.data[1],
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_4615685607825208_reloadGrid_xh1isei7x_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };

  const act_7315092382398562_closeModal_qqqkrool9 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_qqqkrool9_getActionArgs,
    });
  const inst_evh_7315092382398562_close_gimmrorbt =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_qqqkrool9,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_gimmrorbt],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_l7z0vghz2 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_x1v531v1d =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_l7z0vghz2,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_x1v531v1d],
    event: 'reload',
  });
  const act_2248226175642056_setData_3u4uf84el = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_3u4uf84el_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_0mljizx2n =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_3u4uf84el,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_0mljizx2n],
      event: 'selectedRowsChanged',
    });
  const act_2248226175642056_showModal_ucffipva1 =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_ucffipva1_getActionArgs,
    });
  const inst_evh_2248226175642056_command_mv0rzcy96 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_ucffipva1,
      event: 'command',
      when: inst_evh_2248226175642056_command_mv0rzcy96_getWhen,
      displayName: 'showModal',
    });
  const act_2248226175642056_showModal_ztzuz1eqz =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_ztzuz1eqz_getActionArgs,
    });
  const inst_evh_2248226175642056_command_l4e39hc0q =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_ztzuz1eqz,
      event: 'command',
      when: inst_evh_2248226175642056_command_l4e39hc0q_getWhen,
      displayName: 'showModal',
    });
  const act_2248226175642056_showModal_c9khoa9fq =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_c9khoa9fq_getActionArgs,
    });
  const inst_evh_2248226175642056_command_par7ncxnf =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_c9khoa9fq,
      event: 'command',
      when: inst_evh_2248226175642056_command_par7ncxnf_getWhen,
      displayName: 'showModal',
    });
  const inst_evh_2248226175642056_command = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_2248226175642056_command_mv0rzcy96,
      inst_evh_2248226175642056_command_l4e39hc0q,
      inst_evh_2248226175642056_command_par7ncxnf,
    ],
    event: 'command',
  });
  const act_4615685607825208_setData_5r1ewplww = new actionTypes.SetDataAction({
    actionArgs: act_4615685607825208_setData_5r1ewplww_getActionArgs,
  });
  const inst_evh_4615685607825208_changed_4rcaepd8c =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_setData_5r1ewplww,
      event: 'changed',
      displayName: 'setData',
    });
  const act_4615685607825208_setData_h3swu0vmm = new actionTypes.SetDataAction({
    actionArgs: act_4615685607825208_setData_h3swu0vmm_getActionArgs,
  });
  const inst_evh_4615685607825208_changed_6bzboapcu =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_setData_h3swu0vmm,
      event: 'changed',
      displayName: 'setData',
    });
  const act_4615685607825208_reloadGrid_xh1isei7x =
    new actionTypes.ReloadGridAction({
      actionArgs: act_4615685607825208_reloadGrid_xh1isei7x_getActionArgs,
    });
  const inst_evh_4615685607825208_changed_6na5nuctg =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_reloadGrid_xh1isei7x,
      event: 'changed',
      displayName: 'reloadGrid',
    });
  const inst_evh_4615685607825208_changed = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_4615685607825208_changed_4rcaepd8c,
      inst_evh_4615685607825208_changed_6bzboapcu,
      inst_evh_4615685607825208_changed_6na5nuctg,
    ],
    event: 'changed',
  });

  const actions = {
    inst_evh_7315092382398562_close_gimmrorbt,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_x1v531v1d,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_0mljizx2n,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_2248226175642056_command_mv0rzcy96,
    inst_evh_2248226175642056_command_l4e39hc0q,
    inst_evh_2248226175642056_command_par7ncxnf,
    inst_evh_2248226175642056_command,
    inst_evh_4615685607825208_changed_4rcaepd8c,
    inst_evh_4615685607825208_changed_6bzboapcu,
    inst_evh_4615685607825208_changed_6na5nuctg,
    inst_evh_4615685607825208_changed,
  };

  const evh_7315092382398562_close_gimmrorbt = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_gimmrorbt.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_x1v531v1d = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_x1v531v1d.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_0mljizx2n = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_0mljizx2n.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_mv0rzcy96 = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_mv0rzcy96.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_l4e39hc0q = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_l4e39hc0q.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_par7ncxnf = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_par7ncxnf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_changed_4rcaepd8c = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_changed_4rcaepd8c.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_changed_6bzboapcu = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_changed_6bzboapcu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_changed_6na5nuctg = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_changed_6na5nuctg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_changed = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_changed.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="ContractEvents"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpDataGrid
      :name="gridName"
      :headerTitle="documentTitle"
      :items="ds"
      checkbox-row-selection
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      class="page--grid"
      ref="grid"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
      @command="evh_2248226175642056_command($event, {})"
    >
      <template #toolbar>
        <div class="flex-grow-1 d-flex gap-2 align-items-center">
          <DateRangeSelector
            name="contractEventFilters"
            :from="dsParams.date_from"
            :to="dsParams.date_to"
            size="sm"
            @changed="evh_4615685607825208_changed($event, {})"
          >
          </DateRangeSelector>
          <PageHelp path="/general/jobs.html"> </PageHelp>
        </div>
      </template>
    </ItpDataGrid>
  </ItpPage>
</template>
